<script setup>
// NOOVI: gráfico de volume/desempenho na Overview — reaproveita a MESMA fonte
// que /reports/conversation usa (reports#index → V2::Reports::ReportBuilder,
// fetchAccountReport/getAccountReports já prontos no Vuex) e o MESMO wrapper
// de gráfico nativo (shared/components/charts/BarChart.vue, já estilizado
// com os tokens n-*) que ReportContainer.vue usa para cada métrica. 3 abas
// (mockup): "Conversas" mostra DUAS séries (recebidas x resolvidas) no mesmo
// gráfico — o BarChart do @chatwoot/viz já suporta múltiplas séries
// agrupadas, mesmo padrão de ResolutionTrendCard.vue no Captain
// (countChartData: handled + resolved).
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import BarChart from 'shared/components/charts/BarChart.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import OverviewCard from './overview/MetricCard.vue';
import {
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
} from '../helpers/overviewDateFilter';

const { t } = useI18n();
const store = useStore();
const dateFilter = inject(
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
  true
);
const accountReport = useMapGetter('getAccountReports');

// Um range maior (30 dias, 3 meses) com groupBy fixo em "day" vira um
// gráfico de barras ilegível (até ~90 barras). Mesma granularidade adaptativa
// que ReportContainer.vue já usa (GROUP_BY_FILTER), só sem a UI de escolha —
// aqui é automático a partir do span do filtro global.
const groupByPeriod = computed(() => {
  const spanInDays = (dateFilter.to - dateFilter.from) / 86400;
  if (spanInDays <= 31) return 'day';
  if (spanInDays <= 180) return 'week';
  return 'month';
});
const categoryFormat = computed(() =>
  groupByPeriod.value === 'month' ? 'MMM-yyyy' : 'dd-MMM'
);

const METRIC_KEYS = [
  'conversations_count',
  'resolutions_count',
  'avg_first_response_time',
  'avg_resolution_time',
];

const tabs = computed(() => [
  { id: 'conversations', label: t('REPORT.METRICS.CONVERSATIONS.NAME') },
  {
    id: 'first_response',
    label: t('REPORT.METRICS.FIRST_RESPONSE_TIME.NAME'),
  },
  { id: 'resolution', label: t('REPORT.METRICS.RESOLUTION_TIME.NAME') },
]);

const activeTabIndex = ref(0);
const activeTabId = computed(
  () => tabs.value[activeTabIndex.value]?.id || 'conversations'
);
const handleTabChange = tab => {
  activeTabIndex.value = tabs.value.findIndex(t2 => t2.id === tab.id);
};

const pointsFor = key => accountReport.value.data[key] || [];
const categoriesFor = key =>
  pointsFor(key).map(point =>
    format(fromUnixTime(point.timestamp), categoryFormat.value)
  );

const chartData = computed(() => {
  if (activeTabId.value === 'conversations') {
    return {
      categories: categoriesFor('conversations_count'),
      series: [
        {
          id: 'conversations_count',
          label: t('REPORT.METRICS.CONVERSATIONS.NAME'),
          color: 'rgb(var(--blue-9))',
          data: pointsFor('conversations_count'),
        },
        {
          id: 'resolutions_count',
          label: t('REPORT.METRICS.RESOLUTION_COUNT.NAME'),
          color: 'rgb(var(--teal-9))',
          data: pointsFor('resolutions_count'),
        },
      ],
    };
  }

  const key =
    activeTabId.value === 'first_response'
      ? 'avg_first_response_time'
      : 'avg_resolution_time';
  const nameKey =
    activeTabId.value === 'first_response'
      ? 'REPORT.METRICS.FIRST_RESPONSE_TIME.NAME'
      : 'REPORT.METRICS.RESOLUTION_TIME.NAME';

  return {
    categories: categoriesFor(key),
    series: [
      {
        id: key,
        label: t(nameKey),
        color: 'rgb(var(--blue-9))',
        data: pointsFor(key),
      },
    ],
  };
});

const hasData = computed(() => chartData.value.categories.length > 0);

const formatValue = computed(() =>
  activeTabId.value === 'conversations'
    ? value => Number(value).toLocaleString()
    : value => formatTime(value || 0)
);

const isLoading = computed(() => {
  if (activeTabId.value === 'conversations') {
    return (
      accountReport.value.isFetching.conversations_count ||
      accountReport.value.isFetching.resolutions_count
    );
  }
  const key =
    activeTabId.value === 'first_response'
      ? 'avg_first_response_time'
      : 'avg_resolution_time';
  return accountReport.value.isFetching[key];
});

const fetchTrend = () => {
  METRIC_KEYS.forEach(metric => {
    store.dispatch('fetchAccountReport', {
      metric,
      from: dateFilter.from,
      to: dateFilter.to,
      businessHours: dateFilter.businessHours,
      type: 'account',
      groupBy: groupByPeriod.value,
    });
  });
};

onMounted(fetchTrend);
watch(
  () => [dateFilter.from, dateFilter.to, dateFilter.businessHours],
  fetchTrend
);
</script>

<template>
  <OverviewCard
    :is-loading="isLoading"
    :loading-message="t('OVERVIEW_REPORTS.VOLUME_TREND.LOADING_MESSAGE')"
  >
    <template #header>
      <div class="flex items-center gap-2 flex-row flex-wrap min-w-0">
        <h5 class="mb-0 text-n-slate-12 font-medium text-lg break-words">
          {{ t('OVERVIEW_REPORTS.VOLUME_TREND.HEADER') }}
        </h5>
      </div>
      <div
        class="flex flex-row flex-wrap items-center justify-start gap-2 w-full min-w-0 sm:w-auto sm:justify-end"
      >
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="handleTabChange"
        />
      </div>
    </template>
    <BarChart
      v-if="hasData"
      :data="chartData"
      :aria-label="`${tabs[activeTabIndex].label}, ${t('REPORT.GROUPING_OPTIONS.DAY')}`"
      :format-value="formatValue"
      :height="240"
      timeseries
    />
    <div
      v-else
      class="grid h-60 w-full place-content-center text-sm text-n-slate-10"
    >
      {{ t('REPORT.NO_ENOUGH_DATA') }}
    </div>
  </OverviewCard>
</template>
